// R50 — SENDING SOMETHING TO THE CAULDRON, AND THE ONE SURFACE THAT DID IT BY HAND.
//
// R47's finding on a second route, and R26 had already named the surface. Measured 2026-08-01:
// exactly TWO call sites POST `/api/cauldron/add`.
//
//   * `SendToCauldronButton` — the SHARED sender, on eleven surfaces (the journal, the spell page,
//     oils, Moses, the Greater Keys, herbs, the notepaper, the petition, a seal off her shelf). It
//     reads the payload and takes its sentences from the string catalog.
//   * `SealChoiceList` — Lala's seal choice-list, which **hand-typed the whole transport** and with
//     it THREE English refusal sentences: "Sign in to use the Cauldron." and "Could not send to the
//     Cauldron." twice. Two of those already existed as catalog rows, read by the other surface.
//
// R26's own commit named this surface as the odd one out — "the seal choice-list, which does not
// even use the shared sender" — and fixed only the way BACK from the pot. This is 14-B's lesson
// inverted, one route over: **a shared row read by ONE surface is invisible to a check written about
// the other**, so a Spanish member layering three seals was refused in English.
//
// `handleCauldronAdd` answers **HTTP 200 with `{ ok: false }`** for an unrostered kind or a missing
// body ("That cannot be sent to the Cauldron."), and 401 when the session is gone. So this hands
// back an ANSWER rather than a status code, exactly as `saved-readings.ts` does one route over.
//
// This module imports NOTHING (the entry requirement for `scripts/`-loadable law), so the COPY stays
// with whoever holds the catalog.

export const cauldronAddPath = "/api/cauldron/add";

export type CauldronSendBody = {
  // Held to `CAULDRON_KIND_KEYS` (src/magicRoom.js) by the gate, from both sides. The route refuses
  // anything else at 200 with `ok: false`, which is the `refused` answer below.
  kind: string;
  title: string;
  data: unknown;
};

// FOUR answers, and the two in the middle are the ones a status code cannot tell apart.
//   sign-in — the session is gone (401). Nothing she chose is lost; she signs in and taps again.
//   refused — the server DELIBERATELY said no, at 200 or at 4xx/5xx, and usually said why.
//   offline — the request never landed. Nothing was decided; this is transport noise (R18).
export type CauldronSendAnswer =
  | { ok: true }
  | { ok: false; kind: "sign-in" }
  | { ok: false; kind: "refused"; message: string }
  | { ok: false; kind: "offline" };

// The two sentences a surface shows around a send, handed in by whoever has the catalog.
export type CauldronSendCopy = { signIn: string; failed: string };

// `doFetch` is a PARAMETER (R46's locked-door shape) so a gate can drive every answer the route can
// give without a browser, a session or a database.
export async function sendToCauldron(
  body: CauldronSendBody,
  doFetch: typeof fetch = fetch,
): Promise<CauldronSendAnswer> {
  try {
    const res = await doFetch(cauldronAddPath, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (res.status === 401) { await res.body?.cancel?.().catch(() => {}); return { ok: false, kind: "sign-in" }; }
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.ok === false) {
      return { ok: false, kind: "refused", message: typeof data?.message === "string" ? data.message : "" };
    }
    return { ok: true };
  } catch {
    return { ok: false, kind: "offline" };
  }
}

// The server's own sentence wins for a refusal — it is the only one that can say WHY the pot would
// not take it. Never for a 401: the session is gone, and "Sign in" is the only useful thing to say.
export function cauldronSendNote(answer: CauldronSendAnswer, copy: CauldronSendCopy): string {
  if (answer.ok) return "";
  if (answer.kind === "sign-in") return copy.signIn;
  if (answer.kind === "refused" && answer.message) return answer.message;
  return copy.failed;
}

// The button's state, derived from the answer rather than re-typed per surface. A refusal is an
// ERROR, never "sent" — which is the whole finding, in one expression.
export function cauldronSendState(answer: CauldronSendAnswer): "sent" | "error" {
  return answer.ok ? "sent" : "error";
}

// ---------------------------------------------------------------------------------------------
// R64 — THE OTHER THREE. R50 took the SEND and left the pot's own three routes, which is the half
// with the member-facing loss in it.
//
// **THE SERVER CAUGHT ITS OWN DATABASE ERROR AND ANSWERED `{ ok: true, items: [] }`**, so "her pot
// is empty" and "we could not ask" were byte-identical to every caller — the fourth route found
// doing this (R55's `handleVideoProgress`, R60's `handleRoomBackgrounds` and `handleLalaMessages`).
// And here `ok: true` makes it worse than silence rather than equal to it: the client's guard is
// `if (d?.ok) setItems(d.items || [])`, so a failed read did not fail to update her pot, **it
// OVERWROTE it with an empty one.**
//
// **AND THE FAILURE STATE WAS NOT A MISSING SENTENCE BUT A WRONG ONE** — R60's Card Library finding
// word for word. The room reads: *"The Cauldron is empty. From any working in this room, choose Send
// to Cauldron, then come back to bring them together."* That is instructions to go and do the thing
// she already did, printed over a pot the room could not read. The Cauldron is where a member stacks
// what she is about to pay a generation to compile, so an empty one is not a cosmetic state.
//
// The REMOVE and the CLEAR were honest BY ACCIDENT — a refused act leaves the screen matching the
// server, because neither paints ahead of its answer — **and said nothing at all**, which is R25's
// argument applied to a control: a tap that does nothing has no error state. R35 ruled a cauldron
// remove cheap enough not to ASK; **that ruling is about the CONFIRM and never about the ANSWER**
// (R61's line, and it is what keeps the two rounds from contradicting each other).
//
// `doFetch` is a PARAMETER (R46) so a gate drives every answer all three routes can give.
// ---------------------------------------------------------------------------------------------

export const cauldronListPath = "/api/cauldron";
export const cauldronRemovePath = "/api/cauldron/remove";
export const cauldronClearPath = "/api/cauldron/clear";

// The same four-way shape `card-library.ts` and `bookmarks.ts` use, carrying the pot back on a
// success — all three routes answer with the WHOLE list, so there is one shape for all three
// rather than a second answer key for two of them (R51's ruling, applied since).
export type CauldronAnswer<T> =
  | { ok: true; items: T[] }
  | { ok: false; kind: "sign-in" }
  | { ok: false; kind: "refused"; message: string }
  | { ok: false; kind: "offline" };

type CauldronJson = { ok?: boolean; message?: string; items?: unknown };

// **A 200 CARRYING NO `items` ARRAY IS A REFUSAL, NOT AN EMPTY POT** (R18's seal-status lesson,
// R53's `data` guard): the rows come back in the shape the route returns, so an absent array is a
// malformed answer and there is nothing to draw.
const readPot = async <T,>(res: Response): Promise<CauldronAnswer<T>> => {
  if (res.status === 401) return { ok: false, kind: "sign-in" };
  const data = (await res.json().catch(() => ({}))) as CauldronJson;
  if (res.ok && data?.ok !== false && Array.isArray(data?.items)) return { ok: true, items: data.items as T[] };
  return { ok: false, kind: "refused", message: typeof data?.message === "string" ? data.message : "" };
};

export async function listCauldron<T>(doFetch: typeof fetch = fetch): Promise<CauldronAnswer<T>> {
  try {
    return await readPot<T>(await doFetch(cauldronListPath, { cache: "no-store" }));
  } catch {
    return { ok: false, kind: "offline" };
  }
}

const post = async <T,>(path: string, body: unknown, doFetch: typeof fetch): Promise<CauldronAnswer<T>> => {
  try {
    return await readPot<T>(await doFetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }));
  } catch {
    return { ok: false, kind: "offline" };
  }
};

export const removeFromCauldron = <T,>(id: string, doFetch: typeof fetch = fetch) =>
  post<T>(cauldronRemovePath, { id }, doFetch);

export const clearCauldron = <T,>(doFetch: typeof fetch = fetch) =>
  post<T>(cauldronClearPath, {}, doFetch);

// **A SUCCESS SAYS NOTHING** — the pot itself is the answer (the row leaving, the list emptying),
// and a line under a control that worked is noise. Every refusal says something, and the server's
// own words win where it sent any, because only it knows which refusal this was.
export type CauldronPotCopy = { signIn: string; failed: string };

export function cauldronPotNote(answer: CauldronAnswer<unknown>, copy: CauldronPotCopy): string {
  if (answer.ok) return "";
  if (answer.kind === "sign-in") return copy.signIn;
  if (answer.kind === "refused" && answer.message) return answer.message;
  return copy.failed;
}
